import { DEFAULT_REDIRECTION_CODE } from '~~/constants/seo';
import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import type { RedirectionData } from '~~/types/composables/SeoRedirection';
import { adjustUrlForSeoRedirection } from '~~/helpers/url/adjustUrlForSeoRedirection';
import isExternalUrl from '~~/helpers/isExternalUrl/isExternalUrl';
import { getDomainByCountry } from '~~/helpers/domains';
import type { CountryCodeValue } from '~~/types/localization/localizationHelperTypes';

import { ALLOWED_METHODS } from '~~/types/expondoMiddleware';

type SeoRedirectTarget = {
  to: string;
  redirectCode: number;
  external: boolean;
};

type I18nLocalePropertiesLike = {
  localeProperties?: {
    value?: {
      country?: unknown;
    };
  };
};

const SEO_REDIRECTION_EXCLUDED_PATH_PREFIXES = [
  '/_nuxt/',
  '/internal-api/',
];

const isSeoRedirectionPathExcluded = (path: string): boolean => {

  return SEO_REDIRECTION_EXCLUDED_PATH_PREFIXES.some(prefix => path.startsWith(prefix));

};

const prepareSourceUrl = (
  i18n: unknown,
  route: { path: string },
): string => {

  if (isSeoRedirectionPathExcluded(route.path)) {

    return '';

  }

  const country = (i18n as I18nLocalePropertiesLike)?.localeProperties?.value?.country;

  if (typeof country !== 'string') {

    return '';

  }

  const domain = getDomainByCountry(country as CountryCodeValue);

  if (!domain) {

    return '';

  }
  const currentRootDomain = adjustUrlForSeoRedirection(domain);
  const currentPath = route.path;

  return `${currentRootDomain}${currentPath}`;

};

export const useSeoRedirection = () => {

  const i18n = useI18n();
  const route = useRoute();
  const { makeMiddlewareRequest } = useExpondoMiddleware();

  const seoRedirection = async (): Promise<SeoRedirectTarget | null> => {

    if (!import.meta.server) {

      return null;

    }
    try {

      const sourceUrl = prepareSourceUrl(i18n, route);

      if (!sourceUrl) {

        return null;

      }

      const redirectionData = await makeMiddlewareRequest<RedirectionData>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.SEO_REDIRECTION,
        method: ALLOWED_METHODS.get,
        params: { sourceUrl },
      });

      if (redirectionData) {

        return {
          to: redirectionData.targetURL,
          redirectCode: redirectionData.redirectionCode || DEFAULT_REDIRECTION_CODE,
          external: isExternalUrl(redirectionData.targetURL),
        };

      }

    } catch {
      // Expected when no redirection exists
    }

    return null;

  };

  return {
    seoRedirection,
  };

};
